import { consoleApiSlice } from "./consoleApiSlice";
import { createSlice } from "@reduxjs/toolkit";

// ---------------------------------------------------------------------------
// Selected-environment slice (persisted to localStorage like the website slice)
// ---------------------------------------------------------------------------
const getSavedEnv = () => {
  try {
    return localStorage.getItem("selectedAgentMemoryEnv") || null;
  } catch (error) {
    return null;
  }
};

const initialState = {
  selectedEnv: getSavedEnv(),
};

export const agentMemorySlice = createSlice({
  name: "agentMemory",
  initialState,
  reducers: {
    setSelectedAgentMemoryEnv: (state, action) => {
      state.selectedEnv = action.payload;
      try {
        localStorage.setItem("selectedAgentMemoryEnv", action.payload);
      } catch (error) {
        // ignore storage failures
      }
    },
  },
});

export const { setSelectedAgentMemoryEnv } = agentMemorySlice.actions;
export const agentMemoryReducer = agentMemorySlice.reducer;
export const selectSelectedAgentMemoryEnv = (state) =>
  state.agentMemory.selectedEnv;

// ---------------------------------------------------------------------------
// RTK Query endpoints (injected into the shared console api slice)
// ---------------------------------------------------------------------------
export const agentMemoryApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgentMemoryEnvironments: builder.query({
      query: () => "/agent-memory/environments",
      transformResponse: (resp) => resp?.environments || [],
      providesTags: ["AgentMemoryEnvironments"],
    }),

    getAgentMemoryWebsites: builder.query({
      query: (env) => ({ url: "/agent-memory/websites", params: { env } }),
      transformResponse: (resp) => resp || { websites: [], count: 0 },
      providesTags: (result, error, env) => [
        { type: "AgentMemoryWebsites", id: env },
      ],
    }),

    getAgentMemoryFiles: builder.query({
      query: ({ env, website }) => ({
        url: "/agent-memory/files",
        params: { env, website },
      }),
      providesTags: (result, error, { env, website }) => [
        { type: "AgentMemoryFiles", id: `${env}:${website}` },
      ],
    }),

    getAgentMemoryFile: builder.query({
      query: ({ env, key }) => ({
        url: "/agent-memory/file",
        params: { env, key },
      }),
      providesTags: (result, error, { env, key }) => [
        { type: "AgentMemoryFile", id: `${env}:${key}` },
      ],
    }),

    putAgentMemoryFile: builder.mutation({
      query: ({ env, key, contents }) => ({
        url: "/agent-memory/file",
        method: "PUT",
        body: { env, key, contents },
      }),
      invalidatesTags: (result, error, { env, key, website }) => [
        { type: "AgentMemoryFile", id: `${env}:${key}` },
        { type: "AgentMemoryVersions", id: `${env}:${key}` },
        ...(website
          ? [{ type: "AgentMemoryFiles", id: `${env}:${website}` }]
          : []),
      ],
    }),

    getAgentMemoryFileVersions: builder.query({
      query: ({ env, key }) => ({
        url: "/agent-memory/file/versions",
        params: { env, key },
      }),
      transformResponse: (resp) => resp || { versions: [], count: 0 },
      providesTags: (result, error, { env, key }) => [
        { type: "AgentMemoryVersions", id: `${env}:${key}` },
      ],
    }),

    getAgentMemoryFileVersion: builder.query({
      query: ({ env, key, versionId }) => ({
        url: "/agent-memory/file/version",
        params: { env, key, version_id: versionId },
      }),
    }),
  }),
});

export const {
  useGetAgentMemoryEnvironmentsQuery,
  useGetAgentMemoryWebsitesQuery,
  useGetAgentMemoryFilesQuery,
  useGetAgentMemoryFileQuery,
  useLazyGetAgentMemoryFileQuery,
  usePutAgentMemoryFileMutation,
  useGetAgentMemoryFileVersionsQuery,
  useGetAgentMemoryFileVersionQuery,
  useLazyGetAgentMemoryFileVersionQuery,
} = agentMemoryApiSlice;
